import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, Alert, Tag, Popconfirm, Spin, Space } from 'antd';
import { MobileOutlined, SafetyCertificateOutlined, DisconnectOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const GREEN = '#1a6b3c';

/**
 * Association d'une application d'authentification, depuis « Mon profil ».
 *
 * POURQUOI CET ÉCRAN EXISTE. L'association n'était atteignable qu'au vol : le
 * serveur ne la proposait qu'une fois, juste après un code reçu par courriel, sur
 * l'écran de saisie de ce code. Deux conséquences que personne n'avait voulues -
 * on ne pouvait pas s'associer quand on le décidait, et l'agent dont les courriels
 * n'arrivent plus, celui que cette voie de secours vise précisément, ne pouvait
 * pas s'associer du tout puisqu'il ne recevait pas le code qui déclenchait la
 * proposition. Le retrait, lui, n'existait nulle part : l'endpoint était là, aucun
 * écran ne l'appelait.
 *
 * POURQUOI LE MOT DE PASSE. L'enrôlement au login portait sa garantie par
 * construction : une preuve fraîche du second facteur venait d'être donnée.
 * L'ouvrir au profil sans rien demander permettrait à une session volée d'associer
 * le téléphone d'un tiers, transformant ce vol en accès permanent. Le serveur
 * exige donc le mot de passe, et cet écran ne fait que le lui transmettre.
 *
 * POURQUOI LES TROIS CONDITIONS SONT AFFICHÉES SÉPARÉMENT. Un écran qui sait
 * seulement « c'est indisponible » ne peut pas dire pourquoi - et c'est exactement
 * ce qui a fait chercher un bouton inexistant à un exploitant dont le serveur
 * n'avait simplement pas sa clé de chiffrement.
 */
export default function CarteAuthenticator() {
  const { t } = useTranslation('account');
  const [etat, setEtat] = useState(null);
  const [echec, setEchec] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [motDePasse, setMotDePasse] = useState('');
  const [enrolement, setEnrolement] = useState(null);
  const [code, setCode] = useState('');
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    setEchec(false);
    try {
      const res = await authService.etatAuthenticator();
      setEtat(res?.data || null);
      setEchec(!res?.data);
    } catch {
      setEtat(null);
      setEchec(true);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const demarrer = async () => {
    if (!motDePasse) { toast.error(t('authenticator.messages.saisirMotDePasse')); return; }
    setEnCours(true);
    try {
      const res = await authService.demarrerAuthenticator(motDePasse);
      setEnrolement(res?.data || null);
      setMotDePasse('');
    } catch (e) {
      // Le serveur explique le refus - mot de passe faux, serveur non configuré,
      // organisme qui interdit : on remonte son message tel quel.
      toast.error(e?.response?.data?.message || t('authenticator.messages.demarrageImpossible'));
    } finally {
      setEnCours(false);
    }
  };

  const confirmer = async () => {
    if (!code) { toast.error(t('authenticator.messages.saisirCode')); return; }
    setEnCours(true);
    try {
      await authService.confirmerAuthenticator(code);
      toast.success(t('authenticator.associee'));
      setEnrolement(null);
      setCode('');
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('authenticator.messages.codeRefuse'));
    } finally {
      setEnCours(false);
    }
  };

  const retirer = async () => {
    setEnCours(true);
    try {
      await authService.retirerAuthenticator();
      toast.success(t('authenticator.messages.retiree'));
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('authenticator.messages.retraitImpossible'));
    } finally {
      setEnCours(false);
    }
  };

  // Pas de marge haute : la carte ouvre desormais la colonne de droite et doit
  // s'aligner sur le haut des informations personnelles. L'espacement entre
  // cartes est porte par le `gap` de la colonne.
  const cadre = (contenu) => (
    <div className="cb-card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10, background: '#e8f5ed',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <MobileOutlined style={{ color: GREEN, fontSize: 20 }} />
        </div>
        <div>
          <div style={{ fontWeight: 600, color: '#111827' }}>{t('authenticator.titre')}</div>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, maxWidth: 560 }}>
            {t('authenticator.description')}
          </div>
        </div>
      </div>
      {contenu}
    </div>
  );

  if (chargement) return cadre(<Spin />);

  /* NE RIEN AFFICHER etait la pire des reponses. Quand la lecture de l'etat
     echouait - serveur d'une version anterieure a ces routes, session expiree,
     reseau coupe - la carte disparaissait purement et simplement. L'exploitant
     cherchait alors un ecran qu'on lui avait decrit et qui n'etait nulle part,
     sans le moindre indice. Un echec doit se voir. */
  if (echec || !etat) {
    return cadre(
      <Alert
        type="warning" showIcon
        message={t('authenticator.etatIndisponible.titre')}
        description={(
          <Space direction="vertical" size={8}>
            <span>
              {t('authenticator.etatIndisponible.description')}
            </span>
            <Button size="small" onClick={charger} style={{ borderRadius: 8 }}>
              {t('authenticator.etatIndisponible.reessayer')}
            </Button>
          </Space>
        )}
      />,
    );
  }

  if (!etat.disponible) {
    return cadre(
      <Alert
        type="info" showIcon
        message={t('authenticator.nonDisponible.titre')}
        description={t('authenticator.nonDisponible.description')}
      />,
    );
  }

  if (!etat.autoriseParOrganisme) {
    return cadre(
      <Alert
        type="info" showIcon
        message={t('authenticator.nonAutorisee.titre')}
        description={t('authenticator.nonAutorisee.description')}
      />,
    );
  }

  if (etat.associe) {
    return cadre(
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Tag icon={<SafetyCertificateOutlined />} color="success" style={{ borderRadius: 14, fontSize: 12 }}>
          {t('authenticator.associee')}
        </Tag>
        <div style={{ fontSize: 12.5, color: '#6b7280' }}>
          {t('authenticator.retrait.explication')}
        </div>
        <Popconfirm
          title={t('authenticator.retrait.confirmationTitre')}
          description={t('authenticator.retrait.confirmationDescription')}
          onConfirm={retirer}
          okText={t('authenticator.retrait.retirer')} cancelText={t('authenticator.retrait.annuler')}
          okButtonProps={{ danger: true }}
        >
          <Button danger icon={<DisconnectOutlined />} style={{ borderRadius: 8 }}>
            {t('authenticator.retrait.retirer')}
          </Button>
        </Popconfirm>
      </Space>
    );
  }

  if (enrolement) {
    return cadre(
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div style={{ fontSize: 13, color: '#374151' }}>
          {t('authenticator.enrolement.consigne')}
        </div>
        <div style={{ textAlign: 'center' }}>
          <img
            src={`data:image/png;base64,${enrolement.qrCodeBase64}`}
            alt={t('authenticator.enrolement.qrCodeAlt')}
            style={{ width: 168, height: 168, border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 8, background: '#fff' }}
          />
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
            {t('authenticator.enrolement.impossibleScanner')}
          </div>
          <div style={{
            marginTop: 6, fontFamily: 'ui-monospace, monospace', fontSize: 13, fontWeight: 700,
            letterSpacing: 1, color: GREEN, background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: 8, padding: '8px 12px', wordBreak: 'break-all',
          }}>
            {enrolement.secretBase32}
          </div>
        </div>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={t('authenticator.enrolement.placeholderCode')}
            maxLength={6}
            onPressEnter={confirmer}
          />
          <Button
            type="primary" loading={enCours} onClick={confirmer}
            style={{ background: GREEN, borderColor: GREEN }}
          >
            {t('authenticator.enrolement.confirmer')}
          </Button>
        </Space.Compact>
        <Button type="text" size="small" onClick={() => { setEnrolement(null); setCode(''); }}>
          {t('authenticator.enrolement.annuler')}
        </Button>
      </Space>
    );
  }

  return cadre(
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <div style={{ fontSize: 12.5, color: '#6b7280' }}>
        {t('authenticator.association.consigne')}
      </div>
      <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
        <Input.Password
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
          placeholder={t('authenticator.association.placeholderMotDePasse')}
          onPressEnter={demarrer}
        />
        <Button
          type="primary" loading={enCours} onClick={demarrer}
          style={{ background: GREEN, borderColor: GREEN }}
        >
          {t('authenticator.association.associer')}
        </Button>
      </Space.Compact>
    </Space>
  );
}
